import { motion } from "framer-motion";
import { useState, useEffect, useMemo } from "react";
import Segment from "./Segment";
import { FaChartPie } from "react-icons/fa6";
import { CircularChartProps } from "types/reportTypes";
import { useTranslation } from "react-i18next";

export default function CircularChart({
  data,
  centerTitle = "0",
  centerSubtitle,
  centerIcon: CenterIcon = FaChartPie,
  centerBorderColor,
  className = "",
  size: customSize,
  colors = ["#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7", "#DDA0DD"],
  onSegmentClick,
  totalValue: customTotalValue,
  pdfMode = false,
}: CircularChartProps) {
  const { t } = useTranslation();
  const [size, setSize] = useState(customSize || 700);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isCenterHovered, setIsCenterHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (customSize) return;

    const handleResize = () => {
      const w = window.innerWidth;
      if (w < 480) setSize(320);
      else if (w < 640) setSize(380);
      else if (w < 768) setSize(460);
      else if (w < 1024) setSize(560);
      else setSize(700);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [customSize]);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const enhancedData = useMemo(() => {
    return data.map((item, index) => ({
      ...item,
      color: item.color || colors[index % colors.length],
    }));
  }, [data, colors]);

  const totalValue = useMemo(() => {
    if (customTotalValue !== undefined) {
      return customTotalValue;
    }
    return enhancedData.reduce((sum, item) => sum + item.value, 0);
  }, [enhancedData, customTotalValue]);

  const handleSegmentHover = (index: number | null) => {
    setHoveredIndex(index);
  };

  if (!enhancedData || enhancedData.length === 0) {
    return (
      <div
        className={`flex h-[300px] w-full items-center justify-center lg:h-[500px] ${className}`}
      >
        <div className="text-center">
          <FaChartPie className="mx-auto mb-4 text-6xl text-white/10" />
          <p className="text-lg font-light text-white/40">
            {t("assessment.report.noDataShow")}
          </p>
        </div>
      </div>
    );
  }

  const center = size / 2;
  const outerRadius = size * 0.48;
  const innerRadius = size * 0.18;
  const borderColor = centerBorderColor || enhancedData[0]?.color || "#00ff88";

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.95 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`relative flex w-full items-center justify-center overflow-hidden ${className}`}
    >
      <motion.svg
        viewBox={`0 0 ${size} ${size}`}
        className={`relative z-10 w-full`}
        initial={{ rotate: -10, opacity: 0 }}
        animate={{ rotate: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <defs>
          <radialGradient id="ringGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.02)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.05)" />
          </radialGradient>
        </defs>

        <motion.circle
          cx={center}
          cy={center}
          r={outerRadius + 3}
          fill="none"
          stroke="url(#ringGradient)"
          strokeWidth="1"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeInOut", delay: 0.2 }}
        />

        {enhancedData.map((item, index) => {
          return (
            <Segment
              key={item.id}
              segment={item}
              index={index}
              total={enhancedData.length}
              center={center}
              outerRadius={outerRadius}
              innerRadius={innerRadius}
              isHovered={hoveredIndex === index}
              onHover={() => handleSegmentHover(index)}
              onLeave={() => handleSegmentHover(null)}
              onClick={() => onSegmentClick?.(item, index)}
              totalValue={totalValue}
              delay={index * 0.05}
              pdfMode={pdfMode}
            />
          );
        })}
      </motion.svg>

      <motion.div
        className="absolute z-20"
        style={{
          width: size * 0.22,
          height: size * 0.22,
          inset: 0,
          margin: "auto",
        }}
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          duration: 0.5,
          delay: 0.3,
          ease: [0.34, 1.56, 0.64, 1],
        }}
      >
        <div
          className="absolute inset-[-12px] rounded-full blur-2xl"
          style={{
            background: `radial-gradient(circle, ${borderColor}20, transparent 70%)`,
          }}
        />

        <motion.div
          className="relative flex h-full w-full flex-col items-center justify-center rounded-full bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628] shadow-2xl backdrop-blur-xl"
          style={{
            border: `3px solid ${borderColor}70`,
            boxShadow: `
              0 0 30px ${borderColor}15,
              inset 0 0 30px ${borderColor}10,
              inset 0 -4px 10px rgba(0,0,0,0.4),
              0 10px 30px rgba(0,0,0,0.4)
            `,
          }}
          whileHover={{ scale: 1.03 }}
          transition={{ duration: 0.2 }}
          onMouseEnter={() => setIsCenterHovered(true)}
          onMouseLeave={() => setIsCenterHovered(false)}
        >
          <div
            className="absolute inset-[3px] rounded-full"
            style={{
              border: `1px solid ${borderColor}20`,
            }}
          />

          <div className="absolute inset-[2px] rounded-full border border-white/5" />

          <motion.div
            className="relative mb-1 flex items-center justify-center rounded-full"
            style={{
              width: size * 0.05,
              height: size * 0.05,
              background: `radial-gradient(circle, ${borderColor}20, ${borderColor}5)`,
            }}
            animate={{
              rotate: isCenterHovered ? 360 : 0,
            }}
            transition={{
              duration: 0.4,
              ease: "easeInOut",
            }}
          >
            <CenterIcon
              size={size * 0.1}
              className="text-white/90"
              style={{
                color: borderColor,
              }}
            />
          </motion.div>

          <motion.span
            className="font-black tracking-tight text-white/95"
            style={{ fontSize: size * 0.05 }}
            animate={{
              scale: [1, 1.02, 1],
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            {centerTitle} %
          </motion.span>

          <span
            className="font-bold text-white"
            style={{ fontSize: size * 0.02, marginTop: size * 0.004 }}
          >
            {centerSubtitle}
          </span>

          <motion.div
            className="from-transparent to-transparent absolute bottom-[14%] h-[1px] bg-gradient-to-r via-white/15"
            style={{ width: "40%", left: "30%" }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          />
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
